import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, Sun, Moon } from "lucide-react";
import { motion } from "framer-motion";

function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem("tg-theme") === "dark"; } catch { return false; }
  });
  useEffect(() => { localStorage.setItem("tg-theme", dark ? "dark" : "light"); }, [dark]);
  return [dark, setDark];
}

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [D, setDark] = useDarkMode();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (response.ok) {
        login(data.access_token || data.token, data.user);
        toast.success("Welcome back! 🎉");
        navigate("/dashboard");
      } else {
        toast.error(data.detail || "Login failed");
      }
    } catch {
      toast.error("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const bg        = D ? "#0f172a" : "#f0fdf4";
  const panelBg   = D ? "#1e293b" : "#ffffff";
  const textPri   = D ? "#f1f5f9" : "#1e293b";
  const textSec   = D ? "#94a3b8" : "#64748b";
  const inputBg   = D ? "#0f172a" : "#f8fafc";
  const iBorder   = (f) => focused === f ? "#10b981" : D ? "#334155" : "#e2e8f0";
  const inputText = D ? "#f1f5f9" : "#1e293b";
  const pillBg    = D ? "#0f172a" : "#f0fdf4";

  return (
    <div style={{ minHeight: "100vh", background: bg, display: "flex", fontFamily: "'Nunito', sans-serif", transition: "background 0.3s" }}>

      {/* ── Left decorative panel ── */}
      <div
        style={{ flex: 1, background: "linear-gradient(145deg, #10b981 0%, #059669 35%, #0d9488 70%, #0891b2 100%)", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "48px 40px", position: "relative", overflow: "hidden" }}
        className="hidden md:flex"
      >
        {/* Decorative circles */}
        <div style={{ position: "absolute", width: 380, height: 380, background: "rgba(255,255,255,0.05)", borderRadius: "50%", top: -120, left: -120 }} />
        <div style={{ position: "absolute", width: 240, height: 240, background: "rgba(255,255,255,0.06)", borderRadius: "50%", bottom: 20, right: -80 }} />
        <div style={{ position: "absolute", width: 140, height: 140, background: "rgba(255,255,255,0.04)", borderRadius: "50%", top: "38%", right: 60 }} />
        <div style={{ position: "absolute", width: 80, height: 80, background: "rgba(255,255,255,0.08)", borderRadius: "50%", top: "15%", left: "60%" }} />

        <div style={{ position: "relative", textAlign: "center", color: "white", maxWidth: 400 }}>
          <div style={{ width: 72, height: 72, background: "linear-gradient(135deg,#10b981,#0d9488)", borderRadius: 20, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, boxShadow: "0 8px 32px rgba(16,185,129,0.35)" }}>
            <span style={{ color: "#fff", fontWeight: 900, fontSize: 36 }}>T</span>
          </div>
          <h1 style={{ fontSize: 38, fontWeight: 900, marginBottom: 6, letterSpacing: -0.5 }}>Tax Genius</h1>
          <p style={{ fontSize: 12, opacity: 0.6, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>taxgenius.online · A project by AI-for-India</p>
          <p style={{ fontSize: 16, opacity: 0.85, lineHeight: 1.7, marginBottom: 40 }}>
            Master Indian Income Tax with AI-powered quizzes, 1v1 battles, and earn real certificates.
          </p>

          {/* Stats row */}
          <div style={{ display: "flex", gap: 16, justifyContent: "center", marginBottom: 40 }}>
            {[{ e: "👥", v: "1,000+", l: "Learners" }, { e: "🧠", v: "1,000+", l: "Questions" }, { e: "🏆", v: "5,000+", l: "Quizzes" }].map((s, i) => (
              <div key={i} style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(10px)", borderRadius: 18, padding: "14px 18px", textAlign: "center" }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>{s.e}</div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{s.v}</div>
                <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>{s.l}</div>
              </div>
            ))}
          </div>

          {/* Testimonial */}
          <div style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(12px)", borderRadius: 20, padding: "20px 24px", textAlign: "left" }}>
            <div style={{ fontSize: 28, marginBottom: 8, lineHeight: 1 }}>"</div>
            <p style={{ fontSize: 14, fontStyle: "italic", opacity: 0.9, lineHeight: 1.6, marginBottom: 14 }}>
              Finally understood the difference between Old and New Tax Regime! The quiz format makes it stick.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: "50%", background: "rgba(255,255,255,0.25)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🧑</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>Rahul M.</div>
                <div style={{ fontSize: 11, opacity: 0.7 }}>CA Student, Mumbai</div>
              </div>
              <div style={{ marginLeft: "auto", fontSize: 13 }}>⭐⭐⭐⭐⭐</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div style={{ width: "100%", maxWidth: 500, display: "flex", flexDirection: "column", justifyContent: "center", padding: "40px 36px", background: panelBg, position: "relative", transition: "background 0.3s", boxShadow: D ? "none" : "-8px 0 40px rgba(0,0,0,0.06)" }}>

        {/* Top controls */}
        <div style={{ position: "absolute", top: 24, left: 24, right: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button
            onClick={() => navigate("/")}
            style={{ display: "flex", alignItems: "center", gap: 6, color: textSec, background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600, padding: "6px 12px", borderRadius: 8, transition: "color 0.2s" }}
          >
            <ArrowLeft size={15} /> Back to Home
          </button>
          <button
            onClick={() => setDark(!D)}
            style={{ width: 38, height: 38, borderRadius: "50%", background: D ? "#334155" : "#f1f5f9", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s" }}
          >
            {D ? <Sun size={17} color="#fbbf24" /> : <Moon size={17} color="#64748b" />}
          </button>
        </div>

        <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>

          {/* Mobile logo */}
          <div className="md:hidden" style={{ textAlign: "center", marginBottom: 32 }}>
            <div style={{ width: 60, height: 60, background: "linear-gradient(135deg,#10b981,#0d9488)", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
              <span style={{ color: "#fff", fontWeight: 900, fontSize: 28 }}>T</span>
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: textPri }}>Tax Genius</h2>
          </div>

          {/* Heading */}
          <div style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 30, fontWeight: 900, color: textPri, marginBottom: 6, letterSpacing: -0.5 }}>Welcome back! 👋</h2>
            <p style={{ color: textSec, fontSize: 15, lineHeight: 1.5 }}>Sign in to continue your learning journey</p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Email field */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: textPri, display: "block", marginBottom: 7 }}>
                Email Address
              </label>
              <input
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                onFocus={() => setFocused("email")}
                onBlur={() => setFocused(null)}
                required
                style={{
                  width: "100%", height: 50, borderRadius: 13, border: `2px solid ${iBorder("email")}`,
                  background: inputBg, color: inputText, padding: "0 16px", fontSize: 15,
                  outline: "none", transition: "border-color 0.2s", boxSizing: "border-box",
                  fontFamily: "'Nunito', sans-serif",
                }}
              />
            </div>

            {/* Password field */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: textPri }}>Password</label>
                <Link to="/forgot-password" style={{ fontSize: 13, color: "#10b981", fontWeight: 700, textDecoration: "none" }}>Forgot password?</Link>
              </div>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  onFocus={() => setFocused("password")}
                  onBlur={() => setFocused(null)}
                  required
                  style={{
                    width: "100%", height: 50, borderRadius: 13, border: `2px solid ${iBorder("password")}`,
                    background: inputBg, color: inputText, padding: "0 50px 0 16px", fontSize: 15,
                    outline: "none", transition: "border-color 0.2s", boxSizing: "border-box",
                    fontFamily: "'Nunito', sans-serif",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: textSec, display: "flex", alignItems: "center" }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%", height: 54, borderRadius: 14, marginTop: 24,
                background: loading ? "#6ee7b7" : "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
                color: "white", border: "none", fontWeight: 800, fontSize: 16,
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                transition: "opacity 0.2s", boxShadow: loading ? "none" : "0 6px 24px rgba(16,185,129,0.35)",
                fontFamily: "'Nunito', sans-serif", letterSpacing: 0.3,
              }}
            >
              {loading ? (
                <>
                  <div style={{ width: 20, height: 20, border: "2.5px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                  Signing in...
                </>
              ) : "Sign In →"}
            </button>
          </form>

          {/* Register link */}
          <div style={{ marginTop: 24, textAlign: "center" }}>
            <p style={{ color: textSec, fontSize: 14 }}>
              Don't have an account?{" "}
              <Link to="/register" style={{ color: "#10b981", fontWeight: 700, textDecoration: "none" }}>Create one free</Link>
            </p>
          </div>

          {/* Feature bullets */}
          <div style={{ marginTop: 32, background: pillBg, borderRadius: 16, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 11, transition: "background 0.3s" }}>
            {[
              "🤖  AI-generated questions covering Budget 2025 updates",
              "⚔️  1v1 real-time challenges with friends",
              "🏆  Earn certificates & climb the leaderboard",
            ].map((t, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: textSec, lineHeight: 1.5 }}>
                {t}
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default LoginPage;
